import Layout from '@/components/feature/Layout';
import SeoHead from '@/components/feature/SeoHead';
import JsonLd from '@/components/feature/JsonLd';
import PageHero from '@/components/feature/PageHero';
import FaqAccordion from '@/components/feature/FaqAccordion';
import { faqGroups } from '@/mocks/faq';
import { siteInfo } from '@/mocks/site';

const allItems = faqGroups.flatMap((group) => group.items);

const structuredData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'FAQPage',
      mainEntity: allItems.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://ikinxod.cn/' },
        { '@type': 'ListItem', position: 2, name: '常见问题', item: 'https://ikinxod.cn/faq' },
      ],
    },
  ],
};

export default function Faq() {
  return (
    <>
      <SeoHead
        path="/faq"
        title="常见问题 - 爆瓜福利参与资格与奖励领取答疑"
        description="爆瓜福利常见问题解答：参与资格、任务奖励到账时间、瓜豆有效期、抽奖概率、账号安全等高频疑问一次性说清楚，遇到问题先来这里找答案。"
        keywords="爆瓜福利常见问题,福利活动答疑,奖励到账时间,瓜豆有效期,抽奖概率"
      />
      <JsonLd data={structuredData} />

      <Layout>
        <PageHero
          icon="ri-question-answer-line"
          eyebrow="常见问题"
          title="参与过程中会遇到的问题，基本都在这里"
          desc="我们按类别整理了高频疑问，从参与资格到奖励到账，共 4 大类、12 个问题。建议从自己最关心的那一类开始看。"
          crumbs={[{ label: '首页', path: '/' }, { label: '常见问题' }]}
        >
          <div className="flex flex-wrap gap-3">
            <div className="px-4 py-3 rounded-xl bg-background-50 border border-background-200">
              <p className="text-[11px] text-foreground-500">问题总数</p>
              <p className="text-lg text-foreground-950 font-heading">{allItems.length} 个</p>
            </div>
            <div className="px-4 py-3 rounded-xl bg-background-50 border border-background-200">
              <p className="text-[11px] text-foreground-500">问题分类</p>
              <p className="text-lg text-primary-600 font-heading">{faqGroups.length} 类</p>
            </div>
          </div>
        </PageHero>

        <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-50">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <aside className="lg:col-span-3">
              <nav aria-label="问题分类" className="lg:sticky lg:top-28 bg-background-100 border border-background-200 rounded-2xl p-5">
                <h2 className="text-sm font-medium text-foreground-950">问题分类</h2>
                <ul className="mt-4 flex flex-col gap-1">
                  {faqGroups.map((group, index) => (
                    <li key={group.category}>
                      <a
                        href={`#group-${index}`}
                        className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
                      >
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className={group.icon}></i>
                        </span>
                        {group.category}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>

            <div className="lg:col-span-9 flex flex-col gap-10 md:gap-12">
              {faqGroups.map((group, index) => (
                <section key={group.category} id={`group-${index}`} className="scroll-mt-28">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-xl bg-primary-100 text-primary-700 flex items-center justify-center text-xl">
                      <i className={group.icon}></i>
                    </span>
                    <div>
                      <h2 className="text-lg md:text-xl text-foreground-950">{group.category}</h2>
                      <p className="text-xs text-foreground-500 mt-0.5">共 {group.items.length} 个问题</p>
                    </div>
                  </div>
                  <div className="mt-5">
                    <FaqAccordion items={group.items} defaultOpen={-1} />
                  </div>
                </section>
              ))}

              <section className="bg-secondary-50 border border-secondary-200 rounded-2xl p-6 md:p-8">
                <h2 className="text-lg md:text-xl text-secondary-900">这里没有找到答案？</h2>
                <p className="mt-2 text-sm text-secondary-900/80 leading-relaxed">
                  把你的疑问和遇到的具体情况发到反馈邮箱，我们会在 {siteInfo.hours} 内回复。描述得越具体，处理起来越快。
                </p>
                <a
                  href={`mailto:${siteInfo.email}`}
                  className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-secondary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-secondary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-mail-send-line"></i>
                  {siteInfo.email}
                </a>
              </section>
            </div>
          </div>
        </section>
      </Layout>
    </>
  );
}